import {Inject, Injectable, DOCUMENT} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {MetaService} from '../meta/meta.service';

import {LangSrcService} from '../lang-src/lang-src.service';
import {AuthService} from '../auth/auth.service';
import {environment} from '../../../environments/environment';
import {StorageMap} from '@ngx-pwa/local-storage';
import {ScrollService} from "../scroll/scroll.service";

@Injectable({
  providedIn: 'root'
})
export class PageService {

  currentUrl: string;
  title: string;
  navMenuCollapsed = true;
  displayOnlyMain = false;
  displayPurpleMain = false;

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private authSrv: AuthService,
    private metaSrv: MetaService,
    private router: Router,
    private scrollToSrv: ScrollService,
    private langSrcSrv: LangSrcService,
    private storage: StorageMap
  ) {
  }

  async initialize(): Promise<any> {
    return new Promise(async (resolve) => {
      await this.redirectToCorrectLanguage();
      this.currentUrl = this.router.url;
      this.router.events.subscribe(async (e) => {
        if (e instanceof NavigationEnd) {
          this.metaSrv.setMetas({title: null});
          this.currentUrl = this.router.url;
          this.scrollToSrv.scrollTo({
            target: 'main'
          });
          await this.redirectToCorrectLanguage();
        }
      });
      resolve(undefined);
    });
  }

  async redirectToCorrectLanguage() {
    return new Promise(async (resolve) => {
      /**
       * STEP 0
       * Get url params & user information
       */

        // get search params
      const searchParams = new URLSearchParams(this.doc.defaultView.location.search);

      // get language code from url param
      let urlLanguageCode = this.doc.defaultView.location.pathname.split('/').filter(x => (x && x !== '')).shift();
      urlLanguageCode = (urlLanguageCode) ? urlLanguageCode : this.langSrcSrv.defaultShortCode;

      // check if user is logged
      const isLogged = await this.authSrv.isReallyLogged();

      // get language from localStorage
      const lsLanguage = await this.storage.get('language').toPromise();

      /**
       * STEP 1 (for logged user)
       * Set language for all user states (logged & not logged)
       */
      if (
        (!lsLanguage) ||
        (searchParams.get('forceLang') && parseInt(searchParams.get('forceLang'), 0) === 1)
      ) {

        /**
         * Step 1.a
         * Set language from url params
         * No cookie or force url language param is detected
         */
        await this.storage.set('language', urlLanguageCode).toPromise();

      } else if (
        !isLogged &&
        lsLanguage &&
        lsLanguage !== urlLanguageCode
      ) {

        /**
         * Step 1.b
         * Redirect if cookie language code is different of url language param
         */
        this.redirect(lsLanguage);
        return;

      }

      /**
       * STEP 2 (for logged user)
       * Set language from user language
       */
      const user = await this.authSrv.getUser();
      if (
        user &&
        isLogged &&
        this.doc.defaultView.location.pathname !== '/' &&
        (
          user.langSrc.shortCode !== lsLanguage ||
          user.langSrc.shortCode !== urlLanguageCode
        )
      ) {

        await this.storage.set('language', user.langSrc.shortCode).toPromise();
        this.redirect(user.langSrc.shortCode);
        return;

      }

      // set current code in language source service
      this.langSrcSrv.currentCode = await this.storage.get('language').toPromise();
      resolve(undefined);
    });
  }

  redirect(code: any) {
    const path = this.doc.defaultView.location.pathname.split('/');
    path[1] = code;
    this.doc.defaultView.location.href = `${this.doc.defaultView.location.protocol}//${this.doc.defaultView.location.host}${path.join('/')}`;
  }

  redirectToFront() {
    window.open(`${environment.wwwEndpoint}${this.langSrcSrv.current.shortCode}`, '_self');
  }
}
